<template>
  <div class="settings-design">
    <div class="card">
      <div class="card-header">
        <div>
          <div class="card-title"><svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>Report branding</div>
          <div class="card-subtitle">Choose how the PDF is branded when sent to property owners</div>
        </div>
      </div>
      <div class="card-body">
        <div v-if="brandingLoading" class="loading-row">Loading branding…</div>
        <template v-else>
          <div class="radio-group" style="margin-bottom:16px;">
            <label class="radio-opt" :class="{ active: branding.branding_mode === 'invoxis' }" @click="branding.branding_mode = 'invoxis'">
              <div class="radio-dot-wrap"></div>
              <div class="radio-opt-text">
                <strong>Use Invoxis branding</strong>
                <span>Invoxis logo + "Powered by Invoxis" bar on every page. Recommended when you have not uploaded your own logo yet — reports still look polished.</span>
              </div>
            </label>
            <label class="radio-opt" :class="{ active: branding.branding_mode === 'agency', disabled: !hasCompanyLogo }" @click="hasCompanyLogo ? branding.branding_mode = 'agency' : null">
              <div class="radio-dot-wrap"></div>
              <div class="radio-opt-text">
                <strong>Use my company branding</strong>
                <span v-if="hasCompanyLogo">Full white-label: your company logo, your custom footer, zero mention of Invoxis.</span>
                <span v-else style="color:var(--red);">Upload a logo in the Company tab first to enable white-label mode.</span>
              </div>
            </label>
          </div>

          <div v-if="branding.branding_mode === 'agency'">
            <label class="field-label" style="display:block;margin-bottom:6px;">Footer text</label>
            <textarea v-model="branding.pdf_footer_text" rows="3" class="footer-textarea" placeholder="e.g. Coastal Holiday Lets Ltd · 1 Harbour Way, Poole · VAT GB 123 4567 89 · hello@yourcompany.co.uk"></textarea>
            <div class="field-hint" style="margin-top:4px;">Shown at the bottom of every page of your reports. Blank = no footer bar.</div>
          </div>

          <div class="btn-row-actions">
            <button class="btn btn-outline" type="button" @click="resetBranding" :disabled="brandingSaving || !brandingDirty">Reset to saved</button>
            <button class="btn btn-primary" type="button" @click="saveBranding" :disabled="brandingSaving || !brandingDirty">{{ brandingSaving ? 'Saving…' : 'Save branding' }}</button>
          </div>
        </template>
      </div>
    </div>

    <!-- Statement Layout (Lodgify UK #2) — agency-wide toggles override
         every per-property flag. Live preview mirrors the "Management Fees &
         Supplied Services" section of the owner statement. -->
    <div class="card">
      <div class="card-header">
        <div>
          <div class="card-title"><svg viewBox="0 0 24 24"><path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h16"/></svg>Statement Layout</div>
          <div class="card-subtitle">Choose which sections appear on every owner statement</div>
        </div>
      </div>
      <div class="card-body">
        <div v-if="layoutLoading" class="loading-row">Loading Statement Layout…</div>
        <template v-else>
          <div class="layout-grid">
            <div class="layout-toggles">
              <div class="toggle-row">
                <div class="toggle-info">
                  <strong>Cleaning</strong>
                  <span>When OFF: not calculated, not shown on the statement</span>
                </div>
                <label class="toggle"><input type="checkbox" v-model="layout.show_cleaning_section"/><div class="toggle-track"></div><div class="toggle-thumb"></div></label>
              </div>
              <div class="toggle-row">
                <div class="toggle-info">
                  <strong>Laundry &amp; Linen</strong>
                  <span>When OFF: not calculated, not shown on the statement</span>
                </div>
                <label class="toggle"><input type="checkbox" v-model="layout.show_laundry_section"/><div class="toggle-track"></div><div class="toggle-thumb"></div></label>
              </div>
              <div class="toggle-row">
                <div class="toggle-info">
                  <strong>Consumables &amp; Guest Supplies</strong>
                  <span>When OFF: not calculated, not shown on the statement</span>
                </div>
                <label class="toggle"><input type="checkbox" v-model="layout.show_consumables_section"/><div class="toggle-track"></div><div class="toggle-thumb"></div></label>
              </div>
            </div>

            <div class="layout-preview">
              <div class="preview-label">Preview — Management Fees &amp; Supplied Services</div>
              <div class="preview-frame">
                <div class="preview-row preview-row-header">
                  <span>Management Fee</span>
                  <span>£120.00</span>
                </div>
                <div v-if="layout.show_consumables_section" class="preview-row">
                  <span>Consumables &amp; Guest Supplies</span>
                  <span>£20.00</span>
                </div>
                <div v-if="layout.show_laundry_section" class="preview-row">
                  <span>Laundry &amp; Linen Supply</span>
                  <span>£45.00</span>
                </div>
                <div v-if="layout.show_cleaning_section" class="preview-row">
                  <span>Cleaning (Supplied Service)</span>
                  <span>£90.00</span>
                </div>
                <div class="preview-row preview-row-total">
                  <span>Sub-total</span>
                  <span>£{{ previewSubTotal.toFixed(2) }}</span>
                </div>
              </div>
              <div class="preview-note">Toggles apply to every property. A per-property Consumables toggle in Property Settings can only disable further — it never re-enables a section hidden here.</div>
            </div>
          </div>

          <div class="btn-row-actions">
            <button class="btn btn-outline" type="button" @click="resetLayout" :disabled="layoutSaving || !layoutDirty">Reset to saved</button>
            <button class="btn btn-primary" type="button" @click="saveLayout" :disabled="layoutSaving || !layoutDirty">{{ layoutSaving ? 'Saving…' : 'Save Statement Layout' }}</button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ReportsTab',
  inject: {
    notify: { default: null },
  },
  data() {
    return {
      layoutLoading: true,
      layoutSaving: false,
      layout: this.emptyLayout(),
      savedLayout: this.emptyLayout(),
      brandingLoading: true,
      brandingSaving: false,
      branding: this.emptyBranding(),
      savedBranding: this.emptyBranding(),
      hasCompanyLogo: false,
    };
  },
  computed: {
    baseUrl() {
      if (window.location.pathname.startsWith('/dev')) {
        return window.location.origin + '/dev';
      }
      return window.location.origin;
    },
    layoutDirty() {
      return (
        this.layout.show_cleaning_section !== this.savedLayout.show_cleaning_section ||
        this.layout.show_laundry_section !== this.savedLayout.show_laundry_section ||
        this.layout.show_consumables_section !== this.savedLayout.show_consumables_section
      );
    },
    brandingDirty() {
      return (
        this.branding.branding_mode !== this.savedBranding.branding_mode ||
        (this.branding.pdf_footer_text || '') !== (this.savedBranding.pdf_footer_text || '')
      );
    },
    previewSubTotal() {
      let total = 120;
      if (this.layout.show_consumables_section) total += 20;
      if (this.layout.show_laundry_section) total += 45;
      if (this.layout.show_cleaning_section) total += 90;
      return total;
    },
  },
  methods: {
    emptyLayout() {
      return {
        show_cleaning_section: true,
        show_laundry_section: true,
        show_consumables_section: true,
      };
    },
    emptyBranding() {
      return {
        branding_mode: 'invoxis',
        pdf_footer_text: '',
      };
    },
    async loadLayout() {
      this.layoutLoading = true;
      this.brandingLoading = true;
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${this.baseUrl}/api/users/organization-profile/`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = r.data || {};
        const parsed = {
          show_cleaning_section: data.show_cleaning_section !== false,
          show_laundry_section: data.show_laundry_section !== false,
          show_consumables_section: data.show_consumables_section !== false,
        };
        this.layout = { ...parsed };
        this.savedLayout = { ...parsed };
        // Branding — same GET, avoid a second round-trip.
        const brandingParsed = {
          branding_mode: data.branding_mode === 'agency' ? 'agency' : 'invoxis',
          pdf_footer_text: data.pdf_footer_text || '',
        };
        this.branding = { ...brandingParsed };
        this.savedBranding = { ...brandingParsed };
        this.hasCompanyLogo = !!data.company_logo_url;
      } catch (e) {
        console.error('Error loading Statement Layout / branding:', e);
      } finally {
        this.layoutLoading = false;
        this.brandingLoading = false;
      }
    },
    resetBranding() {
      this.branding = { ...this.savedBranding };
    },
    async saveBranding() {
      this.brandingSaving = true;
      try {
        const token = localStorage.getItem('token');
        const form = new FormData();
        // Safety: never send agency mode if no logo (frontend gate, backend
        // enforces the same rule serializer-side).
        const modeToSend = (this.branding.branding_mode === 'agency' && !this.hasCompanyLogo)
          ? 'invoxis'
          : this.branding.branding_mode;
        form.append('branding_mode', modeToSend);
        form.append('pdf_footer_text', this.branding.pdf_footer_text || '');
        const r = await axios.patch(`${this.baseUrl}/api/users/organization-profile/`, form, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        const data = r.data || {};
        const parsed = {
          branding_mode: data.branding_mode === 'agency' ? 'agency' : 'invoxis',
          pdf_footer_text: data.pdf_footer_text || '',
        };
        this.branding = { ...parsed };
        this.savedBranding = { ...parsed };
        this.hasCompanyLogo = !!data.company_logo_url;
        if (this.notify) {
          this.notify('success', 'Saved', 'Branding updated. Applies to every future report.');
        }
      } catch (e) {
        console.error('Error saving branding:', e);
        const msg = e.response?.data?.detail || 'Failed to save branding.';
        if (this.notify) {
          this.notify('error', 'Save failed', msg);
        } else {
          alert(msg);
        }
      } finally {
        this.brandingSaving = false;
      }
    },
    resetLayout() {
      this.layout = { ...this.savedLayout };
    },
    async saveLayout() {
      this.layoutSaving = true;
      try {
        const token = localStorage.getItem('token');
        const form = new FormData();
        form.append('show_cleaning_section', this.layout.show_cleaning_section);
        form.append('show_laundry_section', this.layout.show_laundry_section);
        form.append('show_consumables_section', this.layout.show_consumables_section);
        const r = await axios.patch(`${this.baseUrl}/api/users/organization-profile/`, form, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });
        const data = r.data || {};
        const parsed = {
          show_cleaning_section: data.show_cleaning_section !== false,
          show_laundry_section: data.show_laundry_section !== false,
          show_consumables_section: data.show_consumables_section !== false,
        };
        this.layout = { ...parsed };
        this.savedLayout = { ...parsed };
        if (this.notify) {
          this.notify('success', 'Saved', 'Statement Layout updated. New settings apply to every future report.');
        }
      } catch (e) {
        console.error('Error saving Statement Layout:', e);
        const msg = e.response?.data?.detail || 'Failed to save Statement Layout.';
        if (this.notify) {
          this.notify('error', 'Save failed', msg);
        } else {
          alert(msg);
        }
      } finally {
        this.layoutSaving = false;
      }
    },
  },
  mounted() {
    this.loadLayout();
  },
};
</script>

<style scoped>
.settings-design {
  --accent: #5b4fcf;
  --accent-dim: rgba(91,79,207,0.08);
  --accent-border: rgba(91,79,207,0.25);
  --bg: #f5f4fb;
  --white: #ffffff;
  --text: #1a1a2e;
  --text-mid: #4a4880;
  --text-muted: #9896bb;
  --border: #e8e6f4;
  --shadow-sm: 0 1px 4px rgba(26,24,61,0.07);
  --radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: var(--text);
  -webkit-font-smoothing: antialiased;
}
.settings-design *, .settings-design *::before, .settings-design *::after { box-sizing: border-box; }

.loading-row { color: var(--text-muted); font-size: 13px; padding: 16px 0; }

/* CARDS */
.card { background: var(--white); border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow-sm); overflow: hidden; margin-bottom: 18px; }
.card-header { padding: 16px 22px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
.card-title { font-family: 'Outfit', sans-serif; font-size: 0.9rem; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
.card-title svg { width: 15px; height: 15px; fill: none; stroke: var(--accent); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.card-subtitle { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
.card-body { padding: 20px 22px; }

/* BUTTONS */
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; font-family: 'Inter', sans-serif; transition: opacity 0.15s, transform 0.1s; white-space: nowrap; }
.btn:hover:not(:disabled) { opacity: 0.88; }
.btn:active:not(:disabled) { transform: scale(0.98); }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary { background: var(--accent); color: #fff; }
.btn-outline { background: transparent; color: var(--text-mid); border: 1.5px solid var(--border); }
.btn-row-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border); }

/* TOGGLE */
.toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #f0eef8; gap: 16px; }
.toggle-row:last-child { border-bottom: none; }
.toggle-info strong { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
.toggle-info span { font-size: 11px; color: var(--text-muted); }
.toggle { position: relative; width: 36px; height: 20px; flex-shrink: 0; }
.toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.toggle-track { position: absolute; inset: 0; background: var(--border); border-radius: 10px; cursor: pointer; transition: background 0.2s; }
.toggle input:checked + .toggle-track { background: var(--accent); }
.toggle-thumb { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; background: #fff; border-radius: 50%; transition: transform 0.2s; pointer-events: none; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
.toggle input:checked ~ .toggle-thumb { transform: translateX(16px); }

/* RADIO */
.radio-group { display: flex; flex-direction: column; gap: 8px; }
.radio-opt { display: flex; align-items: flex-start; gap: 10px; padding: 11px 14px; border: 1.5px solid var(--border); border-radius: 9px; cursor: pointer; transition: border-color 0.15s, background 0.15s; }
.radio-opt:not(.disabled):hover { border-color: var(--accent); background: var(--accent-dim); }
.radio-opt.active { border-color: var(--accent); background: var(--accent-dim); }
.radio-opt.disabled { cursor: not-allowed; opacity: 0.55; background: var(--bg); }
.radio-dot-wrap { width: 16px; height: 16px; border-radius: 50%; border: 2px solid var(--border); flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
.radio-opt.active .radio-dot-wrap { border-color: var(--accent); background: var(--accent); }
.radio-opt.active .radio-dot-wrap::after { content: ''; width: 6px; height: 6px; border-radius: 50%; background: #fff; }
.radio-opt-text strong { display: block; font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
.radio-opt.active .radio-opt-text strong { color: var(--accent); }
.radio-opt-text span { font-size: 11px; color: var(--text-muted); line-height: 1.4; }

.footer-textarea { width: 100%; border: 1.5px solid var(--border); border-radius: 8px; padding: 10px 12px; font-family: 'Inter', sans-serif; font-size: 13px; color: var(--text); resize: vertical; min-height: 66px; outline: none; transition: border-color 0.15s, box-shadow 0.15s; }
.footer-textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px rgba(91,79,207,0.1); }
.field-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); }
.field-hint { font-size: 11px; color: var(--text-muted); line-height: 1.5; }

/* STATEMENT LAYOUT */
.layout-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; }
.layout-toggles { display: flex; flex-direction: column; }
.layout-preview { background: var(--bg); border: 1px solid var(--border); border-radius: 10px; padding: 14px 16px; }
.preview-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-muted); margin-bottom: 10px; }
.preview-frame { background: #fff; border: 1px solid var(--border); border-radius: 8px; overflow: hidden; }
.preview-row { display: flex; justify-content: space-between; padding: 8px 12px; font-size: 12px; color: var(--text); border-bottom: 1px solid #f0edf8; }
.preview-row:last-child { border-bottom: none; }
.preview-row-header { font-weight: 700; }
.preview-row-total { background: #f9f8fc; font-weight: 700; border-top: 1.5px solid var(--border); }
.preview-note { font-size: 11px; color: var(--text-muted); margin-top: 10px; line-height: 1.5; }

@media (max-width: 900px) {
  .layout-grid { grid-template-columns: 1fr; }
}
</style>
